import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  User,
  Users,
  DollarSign,
  Phone,
  AlertCircle,
  ArrowLeft,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils';
import { SlotContactSelector, type ContactOption } from '@/components/deals/SlotContactSelector';
import { CountryCodeSelect } from '@/components/common/CountryCodeSelect';

// Types
interface CatalogItem {
  id: string;
  name: string;
  type: string;
  base_price: number;
  dependent_price?: number;
  allows_dependents: boolean;
  is_active: boolean;
  enabled_roles?: string[];
  role_labels?: Record<string, string>;
  description?: string;
}

interface DealContact {
  id: string;
  first_name?: string;
  last_name?: string;
  primary_email?: string;
  primary_phone?: string;
}

interface NewContactData {
  first_name: string;
  last_name: string;
  primary_email: string;
  primary_phone: string;
  primary_phone_country_code: string;
}

type SlotRole = 'applicant' | 'beneficiary' | 'petitioner' | 'dependent' | 'payer' | 'poc' | 'poc_secondary';

interface Slot {
  id: string;
  role: SlotRole;
  label: string;
  slotIndex?: number;
  required: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  
  // Filled data
  contact: ContactOption | null;
  isCreatingNew: boolean;
  newContactData: NewContactData;
  
  // For dependents only
  relationshipType?: string;
}

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  catalogItems: CatalogItem[];
  dealId: string;
  dealContact?: DealContact | null;
  onItemAdded: () => void;
  editingItemId?: string | null;
  onItemUpdated?: () => void;
}

interface SlotCardProps {
  slot: Slot;
  slotHasData: (slot: Slot) => boolean;
  updateSlot: (slotId: string, updates: Partial<Slot>) => void;
  clearSlot: (slotId: string) => void;
  dealContactOption: ContactOption | null;
}

const SlotCard = React.memo(function SlotCard({
  slot,
  slotHasData,
  updateSlot,
  clearSlot,
  dealContactOption,
}: SlotCardProps) {
  const isEmpty = !slotHasData(slot);

  // If creating new contact, show form
  if (slot.isCreatingNew) {
    return (
      <div
        className={cn(
          'border-2 rounded-lg p-4 space-y-3 transition-all',
          'border-dashed border-primary/50 bg-primary/5'
        )}
      >
        {/* Header with back button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{slot.label}</span>
            {['applicant', 'payer', 'poc'].includes(slot.role) && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                Obrigatório p/ fechar
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => updateSlot(slot.id, { isCreatingNew: false, newContactData: createEmptyNewContactData() })}
            className="h-7 text-xs gap-1"
          >
            <ArrowLeft className="h-3 w-3" />
            Voltar
          </Button>
        </div>

        {/* New Contact Form */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Nome *</Label>
              <Input
                value={slot.newContactData.first_name}
                onChange={(e) =>
                  updateSlot(slot.id, {
                    newContactData: {
                      ...slot.newContactData,
                      first_name: e.target.value,
                    },
                  })
                }
                placeholder="João"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sobrenome</Label>
              <Input
                value={slot.newContactData.last_name}
                onChange={(e) =>
                  updateSlot(slot.id, {
                    newContactData: {
                      ...slot.newContactData,
                      last_name: e.target.value,
                    },
                  })
                }
                placeholder="Silva"
                className="h-8 text-sm"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              value={slot.newContactData.primary_email}
              onChange={(e) =>
                updateSlot(slot.id, {
                  newContactData: {
                    ...slot.newContactData,
                    primary_email: e.target.value,
                  },
                })
              }
              placeholder="joao@email.com"
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Telefone</Label>
            <div className="flex gap-2">
              <CountryCodeSelect
                value={slot.newContactData.primary_phone_country_code}
                onChange={(v) =>
                  updateSlot(slot.id, {
                    newContactData: {
                      ...slot.newContactData,
                      primary_phone_country_code: v,
                    },
                  })
                }
                className="w-24 h-8 text-xs"
              />
              <Input
                className="flex-1 h-8 text-sm"
                value={slot.newContactData.primary_phone}
                onChange={(e) =>
                  updateSlot(slot.id, {
                    newContactData: {
                      ...slot.newContactData,
                      primary_phone: e.target.value,
                    },
                  })
                }
                placeholder="(11) 99999-9999"
              />
            </div>
          </div>
        </div>

        {/* Relationship type for dependents - show if has first_name */}
        {slot.role === 'dependent' && slot.newContactData.first_name.trim() && (
          <div className="space-y-1">
            <Label className="text-xs">Tipo de Relacionamento *</Label>
            <Select
              value={slot.relationshipType || ''}
              onValueChange={(v) => updateSlot(slot.id, { relationshipType: v })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {RELATIONSHIP_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    );
  }

  // Normal state: show contact selector
  return (
    <div
      className={cn(
        'border-2 rounded-lg p-4 space-y-3 transition-all',
        isEmpty
          ? 'border-dashed border-amber-400 bg-amber-50/30 dark:bg-amber-900/20'
          : 'border-solid border-border bg-card'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {slot.icon ? (
            <slot.icon className="h-4 w-4 text-muted-foreground" />
          ) : (
            <User className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="font-medium">{slot.label}</span>
          {['applicant', 'payer', 'poc'].includes(slot.role) && (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
              Obrigatório p/ fechar
            </Badge>
          )}
        </div>
        {!isEmpty && !slot.required && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => clearSlot(slot.id)}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Unified Contact Selector */}
      <SlotContactSelector
        value={slot.contact}
        dealContact={dealContactOption}
        onSelectContact={(contact) => updateSlot(slot.id, { contact, isCreatingNew: false })}
        onCreateNew={() => updateSlot(slot.id, { contact: null, isCreatingNew: true })}
        placeholder="Selecionar contato..."
      />

      {/* Relationship type for dependents */}
      {slot.role === 'dependent' && slot.contact && (
        <div className="space-y-1">
          <Label className="text-xs">Tipo de Relacionamento *</Label>
          <Select
            value={slot.relationshipType || ''}
            onValueChange={(v) => updateSlot(slot.id, { relationshipType: v })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
});

const RELATIONSHIP_TYPES = [
  { value: 'spouse', label: 'Cônjuge' },
  { value: 'child', label: 'Filho(a)' },
  { value: 'parent', label: 'Pai/Mãe' },
  { value: 'sibling', label: 'Irmão(ã)' },
];

const createEmptyNewContactData = (): NewContactData => ({
  first_name: '',
  last_name: '',
  primary_email: '',
  primary_phone: '',
  primary_phone_country_code: '+1',
});

function generateSlots(
  catalogItem: CatalogItem,
  numDependents: number,
  enableSecondPoc: boolean
): Slot[] {
  const slots: Slot[] = [];
  const enabledRoles = catalogItem.enabled_roles || ['applicant'];
  const roleLabels = catalogItem.role_labels || {};

  // 1. Applicant slot - always first (required for closing, not for saving)
  if (enabledRoles.includes('applicant') || enabledRoles.includes('primary')) {
    slots.push({
      id: 'applicant',
      role: 'applicant',
      label: roleLabels.applicant || roleLabels.primary || 'Applicant',
      required: false, // Not required for saving, but required for closing deal
      icon: User,
      contact: null,
      isCreatingNew: false,
      newContactData: createEmptyNewContactData(),
    });
  }

  // 2. Beneficiary/Secondary slot
  if (enabledRoles.includes('beneficiary') || enabledRoles.includes('secondary')) {
    slots.push({
      id: 'beneficiary',
      role: 'beneficiary',
      label: roleLabels.beneficiary || roleLabels.secondary || 'Beneficiary',
      required: false,
      icon: User,
      contact: null,
      isCreatingNew: false,
      newContactData: createEmptyNewContactData(),
    });
  }

  // 3. Petitioner slot
  if (enabledRoles.includes('petitioner')) {
    slots.push({
      id: 'petitioner',
      role: 'petitioner',
      label: roleLabels.petitioner || 'Petitioner',
      required: false,
      icon: User,
      contact: null,
      isCreatingNew: false,
      newContactData: createEmptyNewContactData(),
    });
  }

  // 4. Payer slot (required for closing, not for saving)
  slots.push({
    id: 'payer',
    role: 'payer',
    label: 'Payer',
    required: false, // Not required for saving, but required for closing deal
    icon: DollarSign,
    contact: null,
    isCreatingNew: false,
    newContactData: createEmptyNewContactData(),
  });

  // 5. Point of Contact (required for closing, not for saving)
  slots.push({
    id: 'poc',
    role: 'poc',
    label: 'Point of Contact',
    required: false, // Not required for saving, but required for closing deal
    icon: Phone,
    contact: null,
    isCreatingNew: false,
    newContactData: createEmptyNewContactData(),
  });

  // 6. Point of Contact 2 - Optional (toggle)
  if (enableSecondPoc) {
    slots.push({
      id: 'poc_secondary',
      role: 'poc_secondary',
      label: 'Point of Contact 2',
      required: false,
      icon: Phone,
      contact: null,
      isCreatingNew: false,
      newContactData: createEmptyNewContactData(),
    });
  }

  // 7. Dependent slots
  if (catalogItem.allows_dependents && numDependents > 0) {
    for (let i = 1; i <= numDependents; i++) {
      slots.push({
        id: `dependent-${i}`,
        role: 'dependent',
        label: `${roleLabels.dependent || 'Dependent'} ${i}`,
        slotIndex: i,
        required: false,
        icon: Users,
        contact: null,
        isCreatingNew: false,
        newContactData: createEmptyNewContactData(),
        relationshipType: '',
      });
    }
  }

  return slots;
}

export default function AddItemModal({
  isOpen,
  onClose,
  catalogItems,
  dealId,
  dealContact,
  onItemAdded,
  editingItemId,
  onItemUpdated,
}: AddItemModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // State
  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  const [hasDependents, setHasDependents] = useState(false);
  const [numDependents, setNumDependents] = useState(0);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [enableSecondPoc, setEnableSecondPoc] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // State for edit mode: store raw data until slots are generated
  const [pendingEditContacts, setPendingEditContacts] = useState<any[] | null>(null);
  const isLoadingEditData = !!pendingEditContacts;

  // Determine if we're in edit mode
  const isEditMode = !!editingItemId;

  // Selected catalog item
  const selectedCatalog = useMemo(
    () => catalogItems.find((c) => c.id === selectedCatalogId),
    [catalogItems, selectedCatalogId]
  );

  // Regenerate slots when product or dependents or secondPoc change
  // IMPORTANT: Preserve existing slot data when regenerating
  useEffect(() => {
    if (selectedCatalog) {
      setSlots((prevSlots) => {
        const newSlots = generateSlots(
          selectedCatalog,
          hasDependents ? numDependents : 0,
          enableSecondPoc
        );
        
        // Merge new slots with existing data
        const mergedSlots = newSlots.map((newSlot) => {
          const existingSlot = prevSlots.find((s) => s.id === newSlot.id);
          if (existingSlot) {
            return {
              ...newSlot,
              contact: existingSlot.contact,
              isCreatingNew: existingSlot.isCreatingNew,
              newContactData: existingSlot.newContactData,
              relationshipType: existingSlot.relationshipType,
            };
          }
          return newSlot;
        });
        return mergedSlots;
      });
    } else {
      setSlots([]);
    }
  }, [selectedCatalog, hasDependents, numDependents, enableSecondPoc]);

  // Reset when closed (only if not edit mode)
  useEffect(() => {
    if (!isOpen && !editingItemId) {
      setSelectedCatalogId('');
      setHasDependents(false);
      setNumDependents(0);
      setSlots([]);
      setEnableSecondPoc(false);
    }
  }, [isOpen, editingItemId]);

  // Load item data when editing - store contacts in pendingEditContacts
  useEffect(() => {
    if (!editingItemId || !isOpen) {
      setPendingEditContacts(null);
      return;
    }
    
    const loadItemData = async () => {
      try {
        // Fetch item
        const { data: item, error: itemError } = await supabase
          .from('items')
          .select('*')
          .eq('id', editingItemId)
          .single();
        
        if (itemError) throw itemError;

        // Fetch item_contacts with contact details
        const { data: itemContacts, error: contactsError } = await supabase
          .from('item_contacts')
          .select('*, contacts!item_contacts_contact_id_fkey(*)')
          .eq('item_id', editingItemId);
        
        if (contactsError) throw contactsError;

        // Check if there's a secondary POC
        const pocContacts = itemContacts?.filter((ic: any) => ic.is_point_of_contact) || [];
        const hasSecondaryPoc = pocContacts.length > 1;
        setEnableSecondPoc(hasSecondaryPoc);

        // Set catalog - this will trigger slots generation via the other useEffect
        setSelectedCatalogId(item.catalog_id);
        setHasDependents((item.num_dependents || 0) > 0);
        setNumDependents(item.num_dependents || 0);

        // Store contacts to be applied after slots are generated
        setPendingEditContacts(itemContacts || []);

      } catch (error) {
        console.error('Error loading item data for editing:', error);
        toast.error('Erro ao carregar dados do item');
      }
    };

    loadItemData();
  }, [editingItemId, isOpen]);

  // Apply pending edit contacts to slots once slots are generated
  useEffect(() => {
    // Wait for slots to be generated
    if (slots.length === 0) return;
    
    // If no pending contacts, skip
    if (pendingEditContacts === null) return;
    
    // If pending contacts is empty array, just clear it and return
    if (pendingEditContacts.length === 0) {
      setPendingEditContacts(null);
      return;
    }

    // Create a copy of contacts to track which POCs we've placed
    let pocPlacedCount = 0;

    setSlots((prevSlots) => {
      const newSlots = [...prevSlots];
      
      for (const ic of pendingEditContacts) {
        const contactData = (ic as any).contacts;
        if (!contactData) continue;

        const contactOption: ContactOption = {
          id: contactData.id,
          first_name: contactData.first_name || null,
          last_name: contactData.last_name || null,
          primary_email: contactData.primary_email || null,
          primary_phone: contactData.primary_phone || null,
        };

        const role = ic.immigration_role;
        const isPayer = ic.is_payer;
        const isPoc = ic.is_point_of_contact;

        // Fill immigration role slot (applicant, beneficiary, petitioner, dependent)
        if (role) {
          if (role === 'dependent') {
            const depSlot = newSlots.find(s => s.role === 'dependent' && !s.contact);
            if (depSlot) {
              depSlot.contact = contactOption;
              depSlot.relationshipType = '';
            }
          } else {
            const slot = newSlots.find(s => s.role === role);
            if (slot) {
              slot.contact = contactOption;
            }
          }
        }

        // Fill payer slot
        if (isPayer) {
          const payerSlot = newSlots.find(s => s.role === 'payer');
          if (payerSlot) {
            payerSlot.contact = contactOption;
          }
        }

        // Fill POC slot(s)
        if (isPoc) {
          if (pocPlacedCount === 0) {
            const pocSlot = newSlots.find(s => s.role === 'poc');
            if (pocSlot) {
              pocSlot.contact = contactOption;
              pocPlacedCount++;
            }
          } else {
            const pocSecondarySlot = newSlots.find(s => s.role === 'poc_secondary');
            if (pocSecondarySlot) {
              pocSecondarySlot.contact = contactOption;
            }
          }
        }
      }

      return newSlots;
    });

    // Clear pending data so this effect doesn't run again
    setPendingEditContacts(null);
  }, [pendingEditContacts, slots.length]);

  // Check if slot has data
  const slotHasData = useCallback((slot: Slot): boolean => {
    if (slot.contact) return true;
    if (slot.isCreatingNew && slot.newContactData.first_name.trim()) return true;
    return false;
  }, []);

  // Update slot
  const updateSlot = useCallback((slotId: string, updates: Partial<Slot>) => {
    setSlots((prev) => prev.map((s) => (s.id === slotId ? { ...s, ...updates } : s)));
  }, []);

  const clearSlot = useCallback((slotId: string) => {
    setSlots((prev) =>
      prev.map((s) =>
        s.id === slotId
          ? {
              ...s,
              contact: null,
              isCreatingNew: false,
              newContactData: createEmptyNewContactData(),
              relationshipType: '',
            }
          : s
      )
    );
  }, []);

  // Validation
  const validation = useMemo(() => {
    // All required slots filled
    const requiredSlots = slots.filter((s) => s.required);
    const allRequiredFilled = requiredSlots.every(slotHasData);

    // Dependents must have relationship type
    const dependentsValid = slots
      .filter((s) => s.role === 'dependent' && slotHasData(s))
      .every((s) => s.relationshipType);

    return {
      canSave: allRequiredFilled && dependentsValid && !!selectedCatalogId,
      errors: {
        missingRequired: !allRequiredFilled,
        missingRelationship: !dependentsValid,
        noCatalog: !selectedCatalogId,
      },
    };
  }, [slots, selectedCatalogId, slotHasData]);

  // Calculate total price
  const totalPrice = useMemo(() => {
    if (!selectedCatalog) return 0;
    const baseTotal = selectedCatalog.base_price || 0;
    const dependentTotal = hasDependents
      ? (selectedCatalog.dependent_price || 0) * numDependents
      : 0;
    return baseTotal + dependentTotal;
  }, [selectedCatalog, hasDependents, numDependents]);

  // Handle save
  const handleSave = async () => {
    if (!validation.canSave || !selectedCatalog) {
      return;
    }

    try {
      setIsProcessing(true);

      let targetItemId: string;

      if (isEditMode && editingItemId) {
        // UPDATE MODE: Update existing item
        const { error: updateError } = await supabase
          .from('items')
          .update({
            num_dependents: hasDependents ? numDependents : 0,
            dependent_price: selectedCatalog.dependent_price || 0,
          })
          .eq('id', editingItemId);

        if (updateError) throw updateError;
        
        // Delete old item_contacts (will recreate)
        const { error: deleteError } = await supabase
          .from('item_contacts')
          .delete()
          .eq('item_id', editingItemId);

        if (deleteError) throw deleteError;

        targetItemId = editingItemId;
      } else {
        // CREATE MODE: Create new item
        const { data: newItem, error: itemError } = await supabase
          .from('items')
          .insert({
            deal_id: dealId,
            catalog_id: selectedCatalogId,
            quantity: 1,
            unit_price: selectedCatalog.base_price,
            num_dependents: hasDependents ? numDependents : 0,
            dependent_price: selectedCatalog.dependent_price || 0,
            discount_amount: 0,
            discount_percent: 0,
          })
          .select('id')
          .single();

        if (itemError) throw itemError;
        targetItemId = (newItem as { id: string }).id;
      }

      // 2. Build a map of contact_id -> consolidated data
      // This ensures same person with multiple roles (applicant + payer + poc) = 1 record
      const contactDataMap = new Map<string, {
        contactId: string;
        immigrationRole: string | null;
        isPayer: boolean;
        isPointOfContact: boolean;
        dependentOf: string | null;
        relationshipType?: string;
      }>();

      let applicantContactId: string | null = null;

      // First pass: create contacts if needed and gather contact IDs
      const slotContactIds: { slot: Slot; contactId: string }[] = [];
      
      for (const slot of slots) {
        if (!slotHasData(slot)) continue;

        let contactId: string;

        if (slot.contact) {
          contactId = slot.contact.id;
        } else if (slot.isCreatingNew && slot.newContactData.first_name.trim()) {
          // Create new contact
          const { data: newContact, error: contactError } = await supabase
            .from('contacts')
            .insert({
              first_name: slot.newContactData.first_name.trim(),
              last_name: slot.newContactData.last_name.trim() || null,
              primary_email: slot.newContactData.primary_email.trim() || null,
              primary_phone: slot.newContactData.primary_phone.trim() || null,
              primary_phone_country_code: slot.newContactData.primary_phone_country_code,
              contact_type: 'prospect',
            })
            .select('id')
            .single();

          if (contactError) throw contactError;
          contactId = (newContact as { id: string }).id;
        } else {
          continue;
        }

        // Track applicant contact ID for dependent relationships
        if (slot.role === 'applicant') {
          applicantContactId = contactId;
        }

        slotContactIds.push({ slot, contactId });
      }

      // Second pass: consolidate by contact_id
      for (const { slot, contactId } of slotContactIds) {
        const isPayer = slot.role === 'payer';
        const isPointOfContact = slot.role === 'poc' || slot.role === 'poc_secondary';
        const immigrationRole = ['payer', 'poc', 'poc_secondary'].includes(slot.role) 
          ? null 
          : slot.role;

        const existing = contactDataMap.get(contactId);
        
        if (existing) {
          // Merge: add flags, prefer immigration role if set
          contactDataMap.set(contactId, {
            ...existing,
            immigrationRole: existing.immigrationRole || immigrationRole,
            isPayer: existing.isPayer || isPayer,
            isPointOfContact: existing.isPointOfContact || isPointOfContact,
            dependentOf: existing.dependentOf || (slot.role === 'dependent' ? applicantContactId : null),
            relationshipType: existing.relationshipType || slot.relationshipType,
          });
        } else {
          // New entry
          contactDataMap.set(contactId, {
            contactId,
            immigrationRole,
            isPayer,
            isPointOfContact,
            dependentOf: slot.role === 'dependent' ? applicantContactId : null,
            relationshipType: slot.relationshipType,
          });
        }
      }

      // 3. Create item_contacts from consolidated map
      for (const [contactId, data] of contactDataMap) {
        const { error: insertError } = await supabase.from('item_contacts').insert({
          item_id: targetItemId,
          contact_id: contactId,
          immigration_role: data.immigrationRole,
          dependent_of: data.dependentOf,
          is_payer: data.isPayer,
          is_point_of_contact: data.isPointOfContact,
        });
        
        if (insertError) throw insertError;

        // Create family relationship for dependents
        if (data.immigrationRole === 'dependent' && data.relationshipType && applicantContactId) {
          await supabase.from('contact_relationships').upsert(
            {
              from_contact_id: applicantContactId,
              to_contact_id: contactId,
              relationship_kind: 'family',
              relationship_type: data.relationshipType,
            },
            {
              onConflict: 'from_contact_id,to_contact_id',
            }
          );
        }
      }

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['item-contacts', targetItemId] });

      // === Sync case_contacts for all cases linked to this item ===
      if (isEditMode) {
        try {
          // 1. Find all cases linked to this item
          const { data: linkedCases } = await supabase
            .from('cases')
            .select('id')
            .eq('item_id', targetItemId);

          if (linkedCases && linkedCases.length > 0) {
            // 2. Get the freshly created item_contacts
            const { data: freshItemContacts } = await supabase
              .from('item_contacts')
              .select('*')
              .eq('item_id', targetItemId);

            for (const caseObj of linkedCases) {
              // 3. Delete existing case_contacts
              await supabase
                .from('case_contacts')
                .delete()
                .eq('case_id', caseObj.id);

              // 4. Recreate from item_contacts
              if (freshItemContacts && freshItemContacts.length > 0) {
                const newCaseContacts = freshItemContacts.map((ic: any, idx: number) => {
                  let role = ic.immigration_role;
                  if (!role) {
                    if (ic.is_payer) role = 'payer';
                    else if (ic.is_point_of_contact) role = 'point_of_contact';
                    else role = 'beneficiary';
                  }

                  return {
                    case_id: caseObj.id,
                    contact_id: ic.contact_id,
                    role,
                    is_payer: ic.is_payer || false,
                    is_point_of_contact: ic.is_point_of_contact || false,
                    is_primary: ic.immigration_role === 'applicant',
                    is_assigned: true,
                    dependent_of: ic.dependent_of || null,
                    slot_index: ic.immigration_role === 'dependent' ? idx : null,
                  };
                });

                await supabase
                  .from('case_contacts')
                  .insert(newCaseContacts);
              }

              // 5. Invalidate case queries
              queryClient.invalidateQueries({ queryKey: ['case', caseObj.id] });
            }
          }
        } catch (syncError) {
          console.error('Error syncing case_contacts:', syncError);
          // Don't fail the whole operation for sync errors
        }

        toast.success(t('dealItems.itemUpdated') || 'Item atualizado');
        onItemUpdated?.();
      } else {
        toast.success(t('dealItems.itemAdded'));
        onItemAdded();
      }
      onClose();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(isEditMode ? 'Erro ao atualizar item' : t('dealItems.errorAdding'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Convert dealContact to ContactOption format
  const dealContactOption: ContactOption | null = dealContact
    ? {
        id: dealContact.id,
        first_name: dealContact.first_name || null,
        last_name: dealContact.last_name || null,
        primary_email: dealContact.primary_email || null,
        primary_phone: dealContact.primary_phone || null,
      }
    : null;


  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Editar Item' : t('dealItems.addCatalogItem')}
          </DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'Modifique as pessoas e configurações do item'
              : t('dealItems.selectCatalogItemDescription')
            }
          </DialogDescription>
        </DialogHeader>

        {isLoadingEditData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Product selection */}
            <div>
              <Label>Produto</Label>
              <Select 
                value={selectedCatalogId} 
                onValueChange={setSelectedCatalogId}
                disabled={isEditMode}
              >
                <SelectTrigger className={isEditMode ? 'opacity-60' : ''}>
                  <SelectValue placeholder={t('dealItems.selectItem')} />
                </SelectTrigger>
                <SelectContent>
                  {catalogItems
                    .filter((c) => c.is_active)
                    .map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        <div className="flex justify-between items-center gap-4">
                          <span>{item.name}</span>
                          <span className="text-muted-foreground text-sm">
                            {formatCurrency(item.base_price)}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {isEditMode && (
                <p className="text-xs text-muted-foreground mt-1">
                  O produto não pode ser alterado
                </p>
              )}
            </div>

          {/* Slots section - only show after selecting product */}
          {selectedCatalog && (
            <>
              <Separator />

              {/* Product info */}
              <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                <p>
                  <strong>Tipo:</strong> {selectedCatalog.type === 'case' ? 'Caso' : 'Serviço'}
                </p>
                {selectedCatalog.description && (
                  <p className="text-muted-foreground">{selectedCatalog.description}</p>
                )}
              </div>

              {/* People section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span className="font-medium">Pessoas do Caso</span>
                </div>

                {/* Immigration roles slots */}
                {slots
                  .filter((s) => !['dependent', 'payer', 'poc', 'poc_secondary'].includes(s.role))
                  .map((slot) => (
                    <SlotCard 
                      key={slot.id} 
                      slot={slot}
                      slotHasData={slotHasData}
                      updateSlot={updateSlot}
                      clearSlot={clearSlot}
                      dealContactOption={dealContactOption}
                    />
                  ))}

                <Separator className="my-2" />

                {/* Payer and POC slots */}
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-medium text-sm">Pagador e Contato</span>
                </div>
                
                {slots
                  .filter((s) => ['payer', 'poc', 'poc_secondary'].includes(s.role))
                  .map((slot) => (
                    <SlotCard 
                      key={slot.id} 
                      slot={slot}
                      slotHasData={slotHasData}
                      updateSlot={updateSlot}
                      clearSlot={clearSlot}
                      dealContactOption={dealContactOption}
                    />
                  ))}

                {/* Toggle for second POC */}
                <div className="flex items-center justify-between py-2">
                  <Label htmlFor="enableSecondPoc" className="cursor-pointer text-sm">
                    Habilitar segundo ponto de contato
                  </Label>
                  <Switch
                    id="enableSecondPoc"
                    checked={enableSecondPoc}
                    onCheckedChange={setEnableSecondPoc}
                  />
                </div>
              </div>

              {/* Dependents section */}
              {selectedCatalog.allows_dependents && (
                <>
                  <Separator />

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="hasDependents" className="cursor-pointer">
                        Incluir dependentes?
                      </Label>
                      <Switch
                        id="hasDependents"
                        checked={hasDependents}
                        onCheckedChange={(checked) => {
                          setHasDependents(checked);
                          if (!checked) setNumDependents(0);
                        }}
                      />
                    </div>

                    {hasDependents && (
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="numDependents">Quantidade de dependentes</Label>
                          <Input
                            id="numDependents"
                            type="number"
                            min="1"
                            max="10"
                            value={numDependents || ''}
                            onChange={(e) => setNumDependents(parseInt(e.target.value) || 0)}
                            placeholder="Ex: 2"
                            className="mt-1"
                          />
                        </div>

                        {slots
                          .filter((s) => s.role === 'dependent')
                          .map((slot) => (
                            <SlotCard 
                              key={slot.id} 
                              slot={slot}
                              slotHasData={slotHasData}
                              updateSlot={updateSlot}
                              clearSlot={clearSlot}
                              dealContactOption={dealContactOption}
                            />
                          ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Price summary */}
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total do Item:</span>
                  <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                    {formatCurrency(totalPrice)}
                  </span>
                </div>
                {hasDependents && numDependents > 0 && (
                  <div className="mt-2 text-sm text-muted-foreground space-y-1">
                    <p>Base: {formatCurrency(selectedCatalog.base_price)}</p>
                    <p>
                      Dependentes ({numDependents}x):{' '}
                      {formatCurrency((selectedCatalog.dependent_price || 0) * numDependents)}
                    </p>
                  </div>
                )}
              </div>

              {/* Validation warnings */}
              {!validation.canSave && (
                <div className="flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    {validation.errors.missingRequired && <p>Preencha os campos obrigatórios (Applicant, Payer, Point of Contact)</p>}
                    {validation.errors.missingRelationship && (
                      <p>Informe o tipo de relacionamento dos dependentes</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        )}
        {/* Footer */}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!validation.canSave || isProcessing || isLoadingEditData}
            className="bg-primary hover:bg-primary/90"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : isEditMode ? (
              'Salvar Alterações'
            ) : (
              t('common.add')
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
